import React, { useEffect } from 'react'
import { ThemeProvider } from 'styled-components'
import {
  DefaultFeatureFlags,
  PrototypeRendererProvider,
  PrototypeRendererReact,
  PrototypeTestOverlay,
  WebRendererMode,
  usePrototypeStatus,
} from '@sketch-hq/sketch-web-renderer'
import { darkTheme } from '@sketch/global-styles'
import config from '@sketch/env-config'
import { useDevToolsSetting } from '@sketch/devtools'

import {
  usePresentationManifest,
  usePresentationFile,
  PrototypeContextProvider,
  usePrototypeContext,
  useSyncUrl,
  useKeyboardShortcuts,
  useHighlightHotspots,
} from './hooks'
import {
  PrototypeLoadStatus,
  PrototypeRendererStatus,
  PrototypeErrorStatus,
  PrototypeLayout,
  ExitFullScreenButton,
} from './components'

const backgroundColor = { r: 32 / 255, g: 32 / 255, b: 32 / 255, a: 1 }
const IS_CYPRESS = 'Cypress' in window
const IS_DEV_OR_TEST_ENV =
  process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'test'

const SKETCH_WEB_FEATURE_FLAGS: typeof DefaultFeatureFlags = {
  ...DefaultFeatureFlags,
  useDirtyRectsRendering: false,
}

/**
 * Web Renderer Prototype Player integration.
 */
export function PrototypeViewNext() {
  return (
    <ThemeProvider theme={darkTheme}>
      <PrototypeRendererProvider>
        <PrototypeContextProvider>
          <PrototypeLayout>
            <PrototypeViewNextInner />
          </PrototypeLayout>
        </PrototypeContextProvider>
      </PrototypeRendererProvider>
    </ThemeProvider>
  )
}

function PrototypeViewNextInner() {
  useSyncUrl()
  useKeyboardShortcuts()
  const highlightHotspots = useHighlightHotspots()

  const {
    setManifest,
    initialArtboardUUID,
    resizeMode,
    isRouteParamsValid,
    prototypeStructure,
  } = usePrototypeContext()

  const [devToolsReleaseMode] = useDevToolsSetting('webRenderer.releaseMode')
  const [devToolsShowTilesBorders] = useDevToolsSetting(
    'webRenderer.showTilesBorders'
  )
  const [devToolsPrototypeTestOverlay] = useDevToolsSetting(
    'webRenderer.prototypeTestOverlay'
  )

  const rendererStatus = usePrototypeStatus()
  const presFile = usePresentationFile({ artboardUuid: initialArtboardUUID })
  const manifestFile = usePresentationManifest({
    manifestUrl: presFile.page?.presentationFile?.manifestUrl || null,
  })

  const pageUUID = presFile.page?.uuid ?? null
  const imageToken = presFile.page?.presentationFile?.imageToken ?? null
  const fragmentsURLFormat = `${config.web_renderer_image_domain}/presentation/p/${pageUUID}/f/:fragmentId?token=${imageToken}`
  const imagesURLFormat = `${config.web_renderer_image_domain}/presentation/p/${pageUUID}/i/:imageId?token=${imageToken}`

  useEffect(() => {
    if (!manifestFile.manifest) return
    setManifest(manifestFile.manifest)
  }, [setManifest, manifestFile])

  if (presFile.hasError) {
    return (
      <PrototypeErrorStatus
        type={
          presFile.hasNotFoundError ? 'ARTBOARD_NOT_FOUND' : 'GENERIC_SERVER'
        }
      />
    )
  }

  if (manifestFile.status === 'error') {
    return <PrototypeErrorStatus type="GENERIC_SERVER" />
  }

  if (presFile.isLoading || manifestFile.status === 'loading') {
    return <PrototypeLoadStatus />
  }

  if (presFile.isRendering) {
    return <PrototypeLoadStatus text="Generating" />
  }

  if (!prototypeStructure) {
    return <PrototypeLoadStatus />
  }

  if (!isRouteParamsValid) {
    return <PrototypeErrorStatus type="ARTBOARD_NOT_FOUND" />
  }

  const mode = IS_CYPRESS
    ? WebRendererMode.debug
    : IS_DEV_OR_TEST_ENV
    ? devToolsReleaseMode
      ? WebRendererMode.release
      : WebRendererMode.debug
    : WebRendererMode.release

  const showTilesBorders = IS_DEV_OR_TEST_ENV ? devToolsShowTilesBorders : false
  const prototypeTestOverlay = IS_CYPRESS
    ? true
    : IS_DEV_OR_TEST_ENV && devToolsPrototypeTestOverlay

  return (
    <>
      <PrototypeRendererReact
        backgroundColor={backgroundColor}
        locateFile={`${process.env.WEB_RENDERER_ASSETS_FOLDER}/{file}`}
        fragmentsURLFormat={fragmentsURLFormat}
        imagesURLFormat={imagesURLFormat}
        maximumZoomLevel={5}
        minimumZoomLevel={0.01}
        startArtboardUUID={initialArtboardUUID}
        prototypeStructure={prototypeStructure}
        featureFlags={SKETCH_WEB_FEATURE_FLAGS}
        mode={mode}
        showTilesBorders={showTilesBorders}
        highlightHotspots={highlightHotspots}
        resizeMode={resizeMode}
      >
        {prototypeTestOverlay && (
          <PrototypeTestOverlay visualize={devToolsPrototypeTestOverlay} />
        )}
      </PrototypeRendererReact>
      <PrototypeRendererStatus status={rendererStatus} />
      <ExitFullScreenButton />
    </>
  )
}
